// src/pages/DashboardNavigation/hooks/useDashboardNavigation.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getSettings } from "../../../services/settingsServices";

import {
  NAVIGATION_SECTIONS,
  SECTION_IDS_BY_ROLE,
} from "../data/dashboardNavigationData";

import {
  buildNavigationSections,
  flattenNavigationSections,
  getNavigationProgress,
  getNearbyPages,
  getPathLabel,
  normalizePath,
  toRoleKey,
  unwrapSettings,
} from "../utils/dashboardNavigationUtils";

export default function useDashboardNavigation(role) {
  const navigate = useNavigate();
  const location = useLocation();

  const [settings, setSettings] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const roleKey = useMemo(() => toRoleKey(role), [role]);

  useEffect(() => {
    let mounted = true;

    async function fetchSettings() {
      try {
        const data = await getSettings();

        if (!mounted) return;

        setSettings(unwrapSettings(data));
      } catch (error) {
        console.error("getSettings failed:", error);
      } finally {
        if (mounted) {
          setLoaded(true);
        }
      }
    }

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const sectionIds = useMemo(
    () => SECTION_IDS_BY_ROLE[roleKey] || ["user"],
    [roleKey],
  );

  const sections = useMemo(() => {
    if (!loaded) return [];

    return buildNavigationSections({
      sectionsById: NAVIGATION_SECTIONS,
      sectionIds,
      settings,
    });
  }, [loaded, sectionIds, settings]);

  const pages = useMemo(() => flattenNavigationSections(sections), [sections]);

  const currentIndex = useMemo(() => {
    const currentNorm = normalizePath(location.pathname);

    return pages.findIndex((page) => page.norm === currentNorm);
  }, [location.pathname, pages]);

  const total = pages.length;

  const currentPage =
    currentIndex >= 0
      ? pages[currentIndex]
      : {
          path: location.pathname,
          label: getPathLabel(location.pathname),
          groupTitle: "Page actuelle",
          sectionId: null,
          sectionLabel: "Page actuelle",
          index: -1,
        };

  const previousPage = currentIndex > 0 ? pages[currentIndex - 1] : null;

  const nextPage =
    currentIndex >= 0 && currentIndex < pages.length - 1
      ? pages[currentIndex + 1]
      : null;

  const nearbyPages = useMemo(
    () => getNearbyPages(pages, currentIndex, 3),
    [pages, currentIndex],
  );

  const progress = useMemo(
    () => getNavigationProgress({ currentIndex, total }),
    [currentIndex, total],
  );

  useEffect(() => {
    if (!sections.length) return;

    setActiveSectionId((current) => {
      const currentStillExists = sections.some(
        (section) => section.id === current,
      );

      if (currentStillExists) {
        return current;
      }

      return currentPage?.sectionId || sections[0].id;
    });
  }, [sections, currentPage?.sectionId]);

  useEffect(() => {
    if (!sections.length) return;

    const sectionFromCurrentPage = currentPage?.sectionId;

    if (sectionFromCurrentPage) {
      setActiveSectionId(sectionFromCurrentPage);
      return;
    }

    setActiveSectionId((current) => current || sections[0].id);
  }, [location.pathname, sections, currentPage?.sectionId]);

  const goTo = useCallback(
    (path) => {
      if (!path) return;

      navigate(path);
      setDrawerOpen(false);
    },
    [navigate],
  );

  const goPrevious = useCallback(() => {
    if (!previousPage) return;
    goTo(previousPage.path);
  }, [goTo, previousPage]);

  const goNext = useCallback(() => {
    if (!nextPage) return;
    goTo(nextPage.path);
  }, [goTo, nextPage]);

  return {
    loaded,
    sections,
    pages,
    total,

    currentIndex,
    currentPage,
    previousPage,
    nextPage,
    nearbyPages,
    progress,

    drawerOpen,
    setDrawerOpen,

    activeSectionId,
    setActiveSectionId,

    goTo,
    goPrevious,
    goNext,
  };
}